import { useRef, useState } from 'react';
import { Loader2, Paperclip, StickyNote, X } from 'lucide-react';
import type { MetadataNote } from '@/apis/payment';
import { useUploadSalespersonAttachment, useDeleteOrderFile } from '@/hooks/useAdminOrders';
import { DynamicIcon } from '@/components/Icon';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';

interface SalespersonFile {
  id: string;
  name: string;
  url: string;
  uploadedAt: string;
}

interface AddNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (note: string) => void;
  notes?: MetadataNote[];
  isLoadingNotes?: boolean;
  isSaving?: boolean;
  closeOnSave?: boolean;
  orderId: string;
  salespersonFiles?: SalespersonFile[];
}

export function AddNoteDialog({
  open,
  onOpenChange,
  onSave,
  notes,
  isLoadingNotes,
  isSaving,
  closeOnSave = true,
  orderId,
  salespersonFiles = [],
}: AddNoteDialogProps) {
  const [noteText, setNoteText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: uploadFile, isPending: isUploading } = useUploadSalespersonAttachment(orderId);
  const { mutate: deleteFile, isPending: isDeleting } = useDeleteOrderFile(orderId);

  const handleSave = () => {
    onSave?.(noteText);
    setNoteText('');
    if (closeOnSave) onOpenChange(false);
  };

  const handleCancel = () => {
    onOpenChange(false);
    setNoteText('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    files.forEach((file) => uploadFile(file));
    e.target.value = '';
  };

  const showNotes = notes !== undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[520px]">
        <DialogHeader className="border-b border-smoke pb-3">
          <DialogTitle className="text-lg font-semibold text-black">Add a note</DialogTitle>
        </DialogHeader>

        {/* ── Existing notes list ── */}
        {showNotes && (
          <div className="flex flex-col gap-2">
            {isLoadingNotes ? (
              <p className="text-xs text-lightGray">Loading notes…</p>
            ) : notes!.length > 0 ? (
              <div className="max-h-52 overflow-y-auto flex flex-col gap-2 pr-1">
                {notes!.map((note) => (
                  <div key={note.id} className="flex gap-3 rounded-xl border border-smoke bg-smoke1 px-4 py-3">
                    <StickyNote size={14} className="mt-0.5 shrink-0 text-silver" />
                    <div className="flex flex-1 flex-col gap-0.5">
                      <p className="text-sm font-normal text-black">{note.text}</p>
                      <p className="text-xs font-normal text-lightGray">
                        {note.author} · {new Date(note.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-lightGray">No notes yet.</p>
            )}
            <div className="h-px bg-smoke" />
          </div>
        )}

        {/* ── Textarea ── */}
        <div className="py-3">
          <div className="rounded-xl border border-smoke focus-within:border-primary-s500">
            <textarea
              className="w-full resize-none border-0 bg-transparent px-3 py-3 text-sm text-black placeholder:text-lightGray outline-none focus:ring-0"
              rows={5}
              placeholder="Add a note"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
          </div>
        </div>

        {/* ── Uploaded file chips ── */}
        {salespersonFiles.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {salespersonFiles.map((file) => (
              <div
                key={file.id}
                className="border-smoke flex items-center gap-2 rounded-full border bg-neutral-white px-3 py-1.5"
              >
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 transition-opacity hover:opacity-70"
                >
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                    <DynamicIcon name="file_pdf" size={18} />
                  </div>
                  <span className="text-primary-s500 max-w-45 truncate text-sm font-normal">
                    {file.name}
                  </span>
                </a>
                <button
                  type="button"
                  onClick={() => deleteFile(file.id)}
                  disabled={isDeleting}
                  className="text-silver hover:text-danger transition-colors disabled:opacity-50"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── Actions ── */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <div>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileChange} />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="flex items-center gap-1.5 rounded-xl border border-smoke px-4 py-2 text-sm font-normal text-lightGray hover:bg-smoke1 transition-colors disabled:opacity-50"
            >
              {isUploading
                ? <Loader2 size={13} className="animate-spin" />
                : <Paperclip size={13} />
              }
              {isUploading ? 'Uploading...' : 'Add File'}
            </button>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-full border border-smoke bg-smoke1 px-6 py-2 text-sm text-black hover:bg-smoke transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !noteText.trim()}
              className="rounded-full bg-primary-s500 px-6 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
